import { Injectable } from "@angular/core";
import { AppActionTypes } from "@app/store/actions/app.actions";
import {
  LoadGroups,
  LoadGroupsFailure,
  LoadGroupsSuccess,
  JoinGroup,
  JoinGroupSuccess,
  JoinGroupFailure,
  LeaveGroup,
  LeaveGroupSuccess,
  LeaveGroupFailure
} from "@app/store/actions/group.actions";
import { selectGroupsByParams } from "@app/store/selectors/app/group.selectors";
import { MainState } from "@app/store/state";
import { GroupApiService } from "@core/services/api/classic/groups/group-api.service";
import { LoadUser } from "@features/account/store/auth.actions";
import { selectCurrentUser } from "@features/account/store/auth.selectors";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store, select } from "@ngrx/store";
import { of } from "rxjs";
import { catchError, map, switchMap, take } from "rxjs/operators";

@Injectable()
export class GroupEffects {
  loadGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.LOAD_GROUPS),
      switchMap((action: LoadGroups) =>
        this.store.pipe(
          select(selectGroupsByParams(action.payload.params)),
          take(1),
          switchMap(storedGroups =>
            storedGroups && storedGroups.length > 0
              ? of(new LoadGroupsSuccess({ params: action.payload.params, groups: storedGroups }))
              : this.groupApiService.fetchGroups(action.payload.params).pipe(
                  map(groups => new LoadGroupsSuccess({ params: action.payload.params, groups })),
                  catchError(error => of(new LoadGroupsFailure({ params: action.payload.params, error })))
                )
          )
        )
      )
    )
  );

  joinGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.JOIN_GROUP),
      switchMap((action: JoinGroup) =>
        this.groupApiService.joinGroup(action.payload.groupId).pipe(
          map(group => new JoinGroupSuccess({ group })),
          catchError(error => of(new JoinGroupFailure({ error })))
        )
      )
    )
  );

  joinGroupSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.JOIN_GROUP_SUCCESS),
      switchMap(() =>
        this.store.pipe(
          select(selectCurrentUser),
          take(1),
          map(user => {
            if (user) {
              // Reload the current user to get updated group memberships
              return new LoadUser({ id: user.id });
            }
            return { type: "NO_ACTION" };
          })
        )
      )
    )
  );

  leaveGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.LEAVE_GROUP),
      switchMap((action: LeaveGroup) =>
        this.groupApiService.leaveGroup(action.payload.groupId).pipe(
          map(() => new LeaveGroupSuccess({ groupId: action.payload.groupId })),
          catchError(error => of(new LeaveGroupFailure({ error })))
        )
      )
    )
  );

  leaveGroupSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.LEAVE_GROUP_SUCCESS),
      switchMap(() =>
        this.store.pipe(
          select(selectCurrentUser),
          take(1),
          map(user => {
            if (user) {
              // Reload the current user to get updated group memberships
              return new LoadUser({ id: user.id });
            }
            return { type: "NO_ACTION" };
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store<MainState>,
    private groupApiService: GroupApiService
  ) {}
}
